import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { CheckOutlined, MinusOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { countryApi, orgApi, tenantApi } from '../api';
import { useAsync } from '../components/useAsync';
import PageShell from '../components/PageShell';
import BrandingImageInput from '../components/BrandingImageInput';
import DataTable from '../components/DataTable';
import type {
  CountryOption,
  EffectiveRole,
  Organization,
  OrgBranding,
  OrgFeatures,
  OrgType,
  PricingDefaults,
  Tenant,
} from '../types';
import { ORG_LANGUAGE_OPTIONS, ORG_TYPE_LABEL } from '../types';
import {
  FEATURE_SPECS,
  GATEABLE_MODULES,
  MODULE_LABEL,
  PLATFORM_PAGES,
  PLATFORM_ROLES,
  PLATFORM_ROLE_LABEL,
  pageVisibility,
} from '../platform/orgFeatures';
import type { FeatureSpec, PlatformPage } from '../platform/orgFeatures';

/** Everything unrestricted — what a new org gets, and the reset target. */
const UNRESTRICTED: OrgFeatures = {
  disabled_modules: [],
  hidden_inventory_fields: [],
  campaign_types: null,
  visible_metrics: null,
  visible_charts: null,
  inventory_notice_url: null,
  pricing_mode: null,
  pricing_defaults: null,
  individual_billboard_selection: true,
};

/** The card an org gets when rate-card billing is first switched on.
 *
 * Mirrors `app.core.pricing.PRICING_DEFAULTS`: the premiums, rebate and fee are
 * the shape every reseller's card has, while the two that price a specific
 * network start empty because nobody can guess them. The backend rejects a card
 * whose premiums are below 1 or whose percentages are not fractions, so these
 * are the values that make the form valid from the moment it appears.
 */
const NEW_RATE_CARD: PricingDefaults = {
  base_cpm: null,
  share_premiums: { full: 1, half: 1.2, quarter: 1.3 },
  agency_rebate_pct: 0.15,
  tech_fee_pct: 0.05,
};

/** Room an organization name needs before the column is worth reading. */
const ORG_NAME_MIN_WIDTH = 160;

export default function OrganizationsPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [createOpen, setCreateOpen] = useState(false);
  const [attachOrg, setAttachOrg] = useState<Organization | null>(null);
  const [scopeOrg, setScopeOrg] = useState<Organization | null>(null);

  const orgs = useAsync(() => orgApi.list({ page, per_page: perPage }), [page, perPage]);
  const tenants = useAsync(() => tenantApi.list({ per_page: 100 }), []);
  const countries = useAsync(() => countryApi.list(), []);

  const tenantById = new Map((tenants.data?.items ?? []).map((t) => [t.id, t]));

  const columns: ColumnsType<Organization> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 56,
      render: (id: number) => <span className="mono">{id}</span>,
    },
    {
      title: 'Organization',
      key: 'name',
      // The logo its whitelabel site shows (AD-141), so who is branded, and
      // with what, reads off the list. Edited in the drawer's Branding section.
      render: (_, row) => (
        <Space size={8}>
          {row.branding.logo_url && (
            <img
              src={row.branding.logo_url}
              alt=""
              style={{ height: 20, maxWidth: 80, objectFit: 'contain' }}
            />
          )}
          <strong>{row.name}</strong>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'org_type',
      key: 'org_type',
      width: 160,
      render: (t: OrgType) => ORG_TYPE_LABEL[t] ?? t,
    },
    {
      title: 'Whitelabel tenant',
      key: 'tenant',
      width: 170,
      render: (_, row) => {
        if (row.tenant_id === null) {
          return (
            <Space>
              <Tag>Bare domain</Tag>
              <Button size="small" onClick={() => setAttachOrg(row)}>
                Attach
              </Button>
            </Space>
          );
        }
        const tenant = tenantById.get(row.tenant_id);
        return (
          <span className="mono">{tenant ? tenant.subdomain : `tenant ${row.tenant_id}`}</span>
        );
      },
    },
    {
      // What this customer bought. The role gating that also decides what they
      // see is identical for every org, so it lives in the drawer, not here.
      title: 'Product scope',
      key: 'features',
      width: 360,
      render: (_, row) => <ScopeSummary org={row} />,
    },
    {
      // Which measurement markets this customer's workspaces may use. Not part
      // of Product scope: that decides what their console shows, this decides
      // which market's model their inventory is measured against — and it is
      // bounded by what the measurement API accepts, so it is not Seiki's
      // choice to widen.
      title: 'Markets',
      key: 'allowed_countries',
      width: 190,
      render: (_, row) => (
        <Space direction="vertical" size={2}>
          {row.allowed_countries === null ? (
            <Tooltip title="Unrestricted — this org picks up a new market the day it ships, with no edit here.">
              <Tag color="green">All markets</Tag>
            </Tooltip>
          ) : row.allowed_countries.length === 0 ? (
            <Tooltip title="No market allowed, so no workspace can be created for this org.">
              <Tag color="red">None</Tag>
            </Tooltip>
          ) : (
            <Space size={4} wrap>
              {row.allowed_countries.map((c) => (
                <Tag key={c} color={c === row.default_country ? 'blue' : undefined}>
                  {c}
                </Tag>
              ))}
            </Space>
          )}
          {row.default_country ? (
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              default {row.default_country}
            </span>
          ) : (
            <Tooltip title="No default market: every workspace must name its own country, and self-serve signup on this org's address is refused.">
              <span style={{ fontSize: 12, color: '#b45309' }}>no default</span>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'default_language',
      key: 'default_language',
      width: 90,
      render: (value: string) => (
        <Tooltip title="What a NEW member of this org starts in. Existing members keep their own choice.">
          <Tag>{value}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Workspaces',
      dataIndex: 'workspace_count',
      key: 'workspace_count',
      width: 110,
      align: 'center',
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 72,
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag>,
    },
  ];

  // Organization is the one column with no declared width, so it lives on
  // whatever the fixed ones leave behind. Hardcoding the table minimum meant
  // the two drifted apart until the name had 40px and rendered as a sliver;
  // deriving it keeps the name readable whatever columns get added next.
  const minWidth =
    columns.reduce(
      (total, column) => total + (typeof column.width === 'number' ? column.width : 0),
      0,
    ) + ORG_NAME_MIN_WIDTH;

  return (
    <PageShell
      title="Organizations"
      subtitle={
        <>
          The commercial entities that own workspaces. Creating one also creates its default
          workspace, named after the org, and sets what its members get in the platform.
        </>
      }
      actions={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={orgs.reload}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Create organization
          </Button>
        </Space>
      }
    >
      <DataTable<Organization>
        minWidth={minWidth}
        rowKey="id"
        loading={orgs.loading}
        columns={columns}
        dataSource={orgs.data?.items ?? []}
        // A click anywhere on the row opens the org's settings; there is no
        // separate "View & edit" link any more. Controls inside the row
        // (Attach) keep their own action, and selecting text to copy an id or
        // a name does not open the drawer.
        onRow={(row) => ({
          onClick: (event) => {
            const target = event.target as HTMLElement;
            if (target.closest('button, a, input, label, .ant-select, .ant-tag-close-icon')) return;
            if (window.getSelection()?.toString()) return;
            setScopeOrg(row);
          },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize: perPage,
          total: orgs.data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${total} organizations`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPerPage(nextSize);
          },
        }}
      />

      <CreateOrgModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          orgs.reload();
          tenants.reload();
        }}
        countries={countries.data?.items ?? []}
      />
      <AttachTenantModal
        org={attachOrg}
        onClose={() => setAttachOrg(null)}
        onAttached={() => {
          orgs.reload();
          tenants.reload();
        }}
      />
      <ProductScopeDrawer
        org={scopeOrg}
        tenantSubdomain={
          scopeOrg?.tenant_id != null ? tenantById.get(scopeOrg.tenant_id)?.subdomain ?? null : null
        }
        onClose={() => setScopeOrg(null)}
        onSaved={() => orgs.reload()}
        countries={countries.data?.items ?? []}
      />
    </PageShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Reading                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * One row's scope, at a glance.
 *
 * Pages get named tags because "can they see Media Plans?" is the question
 * people arrive with, and an absent tag answers it only if you already know the
 * full list. The other four are counted rather than listed — eight metric names
 * in a table cell is a wall, and the count is what tells you whether to look.
 */
function ScopeSummary({ org }: { org: Organization }) {
  const hidden = new Set(org.features.disabled_modules);
  const restrictions = summarizeRestrictions(org.features);

  return (
    <Space direction="vertical" size={2}>
      <Space size={4} wrap>
        {GATEABLE_MODULES.map((key) =>
          hidden.has(key) ? (
            <Tooltip key={key} title={`${MODULE_LABEL[key]} is hidden from this organization`}>
              <Tag style={{ color: '#9ca3af', textDecoration: 'line-through' }}>
                {MODULE_LABEL[key]}
              </Tag>
            </Tooltip>
          ) : (
            <Tag key={key} color="green">
              {MODULE_LABEL[key]}
            </Tag>
          ),
        )}
      </Space>
      <span style={{ fontSize: 12, color: restrictions ? '#6b7280' : '#9ca3af' }}>
        {restrictions ?? 'Everything else unrestricted'}
      </span>
    </Space>
  );
}

/** "Metrics 1/8 · Charts 8/9 · 5 fields hidden", or null when nothing is set. */
function summarizeRestrictions(features: OrgFeatures): string | null {
  const parts: string[] = [];
  for (const spec of FEATURE_SPECS) {
    if (spec.key === 'disabled_modules') continue; // shown as tags above
    const value = features[spec.key];
    if (spec.kind === 'allow') {
      if (Array.isArray(value)) parts.push(`${spec.label} ${value.length}/${spec.keys.length}`);
    } else if (Array.isArray(value) && value.length > 0) {
      parts.push(`${value.length} ${spec.label.toLowerCase()} hidden`);
    }
  }
  if (features.inventory_notice_url) parts.push('notice link');
  if (features.pricing_mode === 'rate_card') parts.push('rate card');
  if (features.individual_billboard_selection === false) parts.push('networks only');
  return parts.length ? parts.join(' · ') : null;
}

/* -------------------------------------------------------------------------- */
/* Editing                                                                     */
/* -------------------------------------------------------------------------- */

type MatrixRow = PlatformPage & { key: string };

/**
 * Read and change the whole of an org's product scope in one place.
 *
 * The controls and the page grid are deliberately not two screens: the grid
 * re-renders against whatever is currently ticked, so an operator sees what
 * unticking Media Plans costs each role before saving it.
 *
 * The grid also folds in role gating, which this console cannot change, and
 * keeps saying which half is which — someone who reads a dash in a role column
 * as something to fix here will go looking for a switch that does not exist.
 */
function ProductScopeDrawer({
  org,
  tenantSubdomain,
  onClose,
  onSaved,
  countries,
}: {
  org: Organization | null;
  tenantSubdomain: string | null;
  onClose: () => void;
  onSaved: () => void;
  countries: CountryOption[];
}) {
  const [draft, setDraft] = useState<OrgFeatures>(UNRESTRICTED);
  const [saving, setSaving] = useState(false);
  const [markets, setMarkets] = useState<string[] | null>(null);
  const [defaultCountry, setDefaultCountry] = useState<string | null>(null);
  const [savingMarkets, setSavingMarkets] = useState(false);
  const [defaultLanguage, setDefaultLanguage] = useState<string | null>(null);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [branding, setBranding] = useState<OrgBranding | null>(null);
  const [savingBranding, setSavingBranding] = useState(false);

  // Re-seed whenever a different org is opened, and after a save re-reads the
  // list: the drawer is one component reused across rows.
  useEffect(() => {
    if (org) {
      setDraft(org.features);
      setMarkets(org.allowed_countries);
      setDefaultCountry(org.default_country);
      setDefaultLanguage(org.default_language);
      setBranding(org.branding);
    }
  }, [org]);

  const languageDirty = org !== null && defaultLanguage !== org.default_language;

  const marketsDirty =
    org !== null &&
    (JSON.stringify(markets) !== JSON.stringify(org.allowed_countries) ||
      defaultCountry !== org.default_country);

  /**
   * The market options, plus any code this org already stores that is no longer
   * offered.
   *
   * A stored value with no matching option renders as a bare code with no
   * label, so an org backfilled with a market that never went live upstream —
   * jcdecaux's `ZA` — would show an unexplained tag the operator cannot act on.
   * It is listed, labelled, and the label says to drop it. (The server refuses
   * to save it back, which is how the row eventually cleans itself up.)
   */
  // A default outside the list is the one inconsistency that never surfaces as
  // an error on its own — it silently pins every workspace created without a
  // country to a market the org may not operate in. The server refuses it, so
  // clear it here the moment the list stops containing it rather than letting
  // the operator hit a rejection they have to decode.
  useEffect(() => {
    if (markets !== null && defaultCountry && !markets.includes(defaultCountry)) {
      setDefaultCountry(null);
    }
  }, [markets, defaultCountry]);

  const marketOptions = useMemo(() => {
    const options = countries.map((c) => ({
      value: c.iso2,
      label: `${c.name} (${c.iso2})`,
    }));
    if (!countries.length) return options;
    for (const stored of markets ?? []) {
      if (!options.some((o) => o.value === stored)) {
        options.push({
          value: stored,
          label: `${stored} — not a market Seiki measures, remove it`,
        });
      }
    }
    return options;
  }, [countries, markets]);

  /**
   * Markets save separately from product scope, and on purpose.
   *
   * They are two different kinds of decision: product scope is what Seiki sold
   * this customer, markets are where their inventory can be measured at all —
   * and the markets call can be *refused* (it would strand an existing
   * workspace, or names a market the API cannot compute). Folding it into the
   * Save above would mean a rejected country policy also discarded an unrelated
   * metrics edit.
   */
  async function onSaveMarkets() {
    if (!org) return;
    setSavingMarkets(true);
    try {
      await orgApi.setGeoPolicy(org.id, markets, defaultCountry);
      message.success(
        markets === null
          ? `${org.name} may use every market`
          : `${org.name} is restricted to ${markets.join(', ') || 'no market'}`,
      );
      onSaved();
    } catch {
      /* toast already shown; the draft stays so the operator can adjust it */
    } finally {
      setSavingMarkets(false);
    }
  }

  /** Separate from both saves above, for the reason stated on onSaveMarkets: a
   *  rejected language must not discard an unrelated metrics or market edit. */
  async function onSaveLanguage() {
    if (!org || !defaultLanguage) return;
    setSavingLanguage(true);
    try {
      await orgApi.setDefaultLanguage(org.id, defaultLanguage);
      message.success(
        `New ${org.name} members will start in ${
          ORG_LANGUAGE_OPTIONS.find((o) => o.value === defaultLanguage)?.label ??
          defaultLanguage
        }`,
      );
      onSaved();
    } catch {
      /* toast already shown; the draft stays so the operator can adjust it */
    } finally {
      setSavingLanguage(false);
    }
  }

  const brandingDirty =
    org !== null && branding !== null && JSON.stringify(branding) !== JSON.stringify(org.branding);

  /** Its own save, like the language: a rejected image must not discard an
   *  unrelated edit, and the preview is checked before the client's site changes. */
  async function onSaveBranding() {
    if (!org || !branding) return;
    setSavingBranding(true);
    try {
      await orgApi.setBranding(org.id, branding);
      message.success(`Updated ${org.name}'s branding`);
      onSaved();
    } catch {
      /* toast already shown; the draft stays so the operator can adjust it */
    } finally {
      setSavingBranding(false);
    }
  }

  function patchBranding(next: Partial<OrgBranding>) {
    setBranding((current) => (current ? { ...current, ...next } : current));
  }

  const dirty = org !== null && JSON.stringify(draft) !== JSON.stringify(org.features);

  function patch(next: Partial<OrgFeatures>) {
    setDraft((current) => ({ ...current, ...next }));
  }

  async function onSave() {
    if (!org) return;
    setSaving(true);
    try {
      await orgApi.setFeatures(org.id, draft);
      message.success(`Updated what ${org.name} can reach`);
      onSaved();
      onClose();
    } catch {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  }

  const columns: ColumnsType<MatrixRow> = [
    {
      title: 'Page',
      key: 'page',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>
            {row.section && <span style={{ color: '#9ca3af' }}>{row.section} › </span>}
            <strong>{row.label}</strong>
          </span>
          <span className="mono" style={{ fontSize: 12, color: '#9ca3af' }}>
            {row.path}
          </span>
        </Space>
      ),
    },
    ...PLATFORM_ROLES.map((role) => ({
      title: PLATFORM_ROLE_LABEL[role],
      key: role,
      width: 120,
      align: 'center' as const,
      render: (_: unknown, row: MatrixRow) => (
        <VisibilityCell page={row} role={role} disabled={draft.disabled_modules} />
      ),
    })),
  ];

  return (
    <Drawer
      title={org ? `Product scope — ${org.name}` : 'Product scope'}
      width={840}
      open={org !== null}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!dirty}
            onClick={onSave}
          >
            Save
          </Button>
        </Space>
      }
    >
      {org && (
        <>
          <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginTop: 0 }}>
            What this customer bought. Set here and nowhere else — it used to live in the
            whitelabel tenant&apos;s config file, which could say nothing about an organization
            on the bare Seiki domain. Changes reach members the next time they load the
            platform; exports and PDFs pick them up immediately.
          </Typography.Paragraph>

          <Typography.Text strong>Markets</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '4px 0 8px' }}>
            Which measurement markets this org&apos;s workspaces may be pinned to. Every
            workspace has exactly one, and its inventory is measured against that market&apos;s
            model — so this is a data-correctness rule, not a product setting, and only markets
            the measurement API supports are offered.
          </Typography.Paragraph>
          <Radio.Group
            value={markets === null ? 'all' : 'some'}
            onChange={(e) =>
              setMarkets(e.target.value === 'all' ? null : (org?.selectable_countries ?? []))
            }
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="all">All markets</Radio.Button>
            <Radio.Button value="some">Only these</Radio.Button>
          </Radio.Group>
          {markets !== null && (
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Pick the markets this org may use"
              value={markets}
              onChange={setMarkets}
              optionFilterProp="label"
              loading={!countries.length}
              options={marketOptions}
            />
          )}
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
            <strong>All markets</strong> is not the same as ticking every box: only it keeps
            following the platform, so this org can use a new market the day it ships. An empty
            list blocks workspace creation entirely, and a list that would exclude a workspace
            the org already has is refused.
          </Typography.Paragraph>

          <Typography.Text strong style={{ display: 'block', marginTop: 12 }}>
            Default market
          </Typography.Text>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: '100%', marginTop: 8 }}
            placeholder="No default — every workspace must name its country"
            value={defaultCountry ?? undefined}
            onChange={(v) => setDefaultCountry(v ?? null)}
            loading={!countries.length}
            options={(markets === null
              ? (org?.selectable_countries ?? [])
              : markets
            ).map((iso2) => ({
              value: iso2,
              label: countries.find((c) => c.iso2 === iso2)?.name
                ? `${countries.find((c) => c.iso2 === iso2)!.name} (${iso2})`
                : iso2,
            }))}
          />
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
            What a workspace gets when nobody names a market — this org&apos;s default
            workspace, and every self-serve signup on its subdomain, which has no way to ask.
            Leave it empty and signup on this org&apos;s address is refused, and every
            workspace has to state its own country.
          </Typography.Paragraph>

          <Button
            size="small"
            type="primary"
            loading={savingMarkets}
            disabled={!marketsDirty}
            onClick={onSaveMarkets}
          >
            Save markets
          </Button>

          <Divider style={{ margin: '20px 0 16px' }} />

          <Typography.Text strong>Default language</Typography.Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            value={defaultLanguage ?? undefined}
            onChange={(v) => setDefaultLanguage(v)}
            options={ORG_LANGUAGE_OPTIONS}
          />
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
            The language a <strong>new</strong> member of this org starts the console in —
            anyone provisioned here, and every self-serve signup on its subdomain. It does not
            change the language of the people already in the org: that is each person&apos;s
            own choice on their account page, and this is not the place to overrule it.
          </Typography.Paragraph>

          <Button
            size="small"
            type="primary"
            loading={savingLanguage}
            disabled={!languageDirty}
            onClick={onSaveLanguage}
          >
            Save language
          </Button>

          <Divider style={{ margin: '20px 0 16px' }} />
          <Typography.Text strong>Branding</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '4px 0 8px' }}>
            {tenantSubdomain ? (
              <>
                Shown on <span className="mono">{tenantSubdomain}.seiki.co</span>: its login page,
                the console, the report PDFs and its emails. Empty means Seiki&apos;s own. Upload,
                check the preview, then save.
              </>
            ) : (
              <>
                This organization has no whitelabel site, so its branding is not shown anywhere
                until a tenant is attached.
              </>
            )}
          </Typography.Paragraph>
          {branding && (
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              <div>
                <Typography.Text>Logo</Typography.Text>
                <BrandingImageInput
                  orgId={org.id}
                  kind="logo"
                  value={branding.logo_url}
                  onChange={(v) => patchBranding({ logo_url: v })}
                />
              </div>
              <div>
                <Typography.Text>Logomark</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  symbol-only, for the collapsed sidebar
                </Typography.Text>
                <BrandingImageInput
                  orgId={org.id}
                  kind="logo-mark"
                  value={branding.logo_mark_url}
                  onChange={(v) => patchBranding({ logo_mark_url: v })}
                />
              </div>
              <div>
                <Typography.Text>Favicon</Typography.Text>
                <BrandingImageInput
                  orgId={org.id}
                  kind="favicon"
                  value={branding.favicon_url}
                  onChange={(v) => patchBranding({ favicon_url: v })}
                />
              </div>
              <Checkbox
                checked={branding.pdf_logo_white_on_dark}
                onChange={(e) => patchBranding({ pdf_logo_white_on_dark: e.target.checked })}
              >
                Show the logo in white on the PDF&apos;s dark header
              </Checkbox>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                Only for a logo in a single dark colour, which otherwise disappears into that
                header. A multicolour logo would lose its colours.
              </Typography.Paragraph>

              <Typography.Text strong style={{ marginTop: 6 }}>
                Emails
              </Typography.Text>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                Invitations, password resets, verification codes and campaign notifications
                sent to this organization&apos;s people. Empty means Seiki&apos;s. The
                &ldquo;powered by&rdquo; footer is always Seiki&apos;s logo.
              </Typography.Paragraph>
              <div>
                <Typography.Text>Email banner</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  top of every email, about 1200×200
                </Typography.Text>
                <BrandingImageInput
                  orgId={org.id}
                  kind="email-header"
                  value={branding.email_header_url}
                  onChange={(v) => patchBranding({ email_header_url: v })}
                />
              </div>
            </Space>
          )}
          <Button
            size="small"
            type="primary"
            style={{ marginTop: 12 }}
            loading={savingBranding}
            disabled={!brandingDirty}
            onClick={onSaveBranding}
          >
            Save branding
          </Button>

          <Divider style={{ margin: '20px 0 16px' }} />

          {FEATURE_SPECS.map((spec) => (
            <FeatureControl
              key={spec.key}
              spec={spec}
              value={draft[spec.key]}
              onChange={(next) => patch({ [spec.key]: next } as Partial<OrgFeatures>)}
            />
          ))}

          <Divider style={{ margin: '20px 0 16px' }} />

          <Typography.Text strong>Inventory reference sheet</Typography.Text>
          <Input
            style={{ marginTop: 8 }}
            allowClear
            placeholder="https://docs.google.com/spreadsheets/…"
            value={draft.inventory_notice_url ?? ''}
            onChange={(e) => patch({ inventory_notice_url: e.target.value || null })}
            status={
              draft.inventory_notice_url && !draft.inventory_notice_url.startsWith('https://')
                ? 'error'
                : undefined
            }
          />
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
            Shown as a banner on My Inventory, linking to this customer&apos;s own reference
            sheet. Must be <span className="mono">https://</span> — the backend refuses anything
            else, so a link that cannot render cannot be stored.
          </Typography.Paragraph>

          <Divider style={{ margin: '20px 0 16px' }} />

          <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
            <Typography.Text strong>Individual billboard selection</Typography.Text>
            <Switch
              checked={draft.individual_billboard_selection !== false}
              onChange={(on) => patch({ individual_billboard_selection: on })}
            />
          </Space>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
            On is the platform default: in the campaign wizard, contributors and admins can pick
            billboards one by one. Off keeps everyone in this org to whole networks, whatever
            their workspace role. Billboards a network marks as removable can still be taken out
            of it.
          </Typography.Paragraph>

          <Divider style={{ margin: '20px 0 16px' }} />

          <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
            <Typography.Text strong>Reseller rate card</Typography.Text>
            <Switch
              checked={draft.pricing_mode === 'rate_card'}
              onChange={(on) =>
                patch(
                  on
                    ? {
                        pricing_mode: 'rate_card',
                        // Switching on without a card would save a mode the
                        // backend then prices nothing from.
                        pricing_defaults: draft.pricing_defaults ?? { ...NEW_RATE_CARD },
                      }
                    : // Off clears the card too. Leaving it behind would make
                      // "off" mean two different stored states.
                      { pricing_mode: null, pricing_defaults: null },
                )
              }
            />
          </Space>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
            For customers who resell space and quote their own clients from this platform.
            Off is the platform default: a campaign carries a budget and the console reports a
            CPM. On inverts that — a CPM goes in and a price comes out, and the scenario page
            gains a Proposal tab. Everything else about the account is unchanged either way.
            <br />
            <br />
            This switch is all there is to set here. The card itself — base CPM, share
            premiums, agency rebate, technical fee — is the customer&apos;s own commercial
            pricing, and they set it on the Proposal tab of any scenario. Switching this on
            leaves them an empty card to fill in; until they do, their scenarios show no
            proposal.
          </Typography.Paragraph>

          <Divider style={{ margin: '20px 0 16px' }} />

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="The grid below is only half yours to change"
            description={
              <>
                Pages follow the switches above. Everything else in it is fixed in the
                platform&apos;s own code and identical for every customer — a dash in a role
                column is not something that can be granted from here. Seiki staff pass every
                role gate regardless.
              </>
            }
          />

          <Table<MatrixRow>
            size="small"
            rowKey="key"
            pagination={false}
            columns={columns}
            dataSource={PLATFORM_PAGES.map((p) => ({ ...p, key: p.key }))}
          />

          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
            Roles are computed, not stored: an org admin reads as Admin, and everyone else takes
            the label their workspace membership implies — so the same person can be a Team
            member in one workspace and External in another, and sees the corresponding column
            there.
          </Typography.Paragraph>
        </>
      )}
    </Drawer>
  );
}

/**
 * One list setting.
 *
 * Both shapes are shown as "what they get", because that is the question, but
 * they are not the same underneath and the control says so. A block-list has two
 * states and stores the complement of what is ticked. An allow-list has three:
 * *All* stores `null`, which keeps following the platform as it ships new
 * metrics, and is genuinely different from ticking every box — that stores an
 * exhaustive list and freezes the org at today's set.
 */
function FeatureControl({
  spec,
  value,
  onChange,
}: {
  spec: FeatureSpec;
  value: string[] | null;
  onChange: (next: string[] | null) => void;
}) {
  const options = spec.keys.map((key) => ({ value: key, label: spec.labels[key] ?? key }));

  if (spec.kind === 'block') {
    const hidden = new Set(value ?? []);
    const shown = spec.keys.filter((k) => !hidden.has(k));
    return (
      <div style={{ marginBottom: 18 }}>
        <Typography.Text strong>{spec.label}</Typography.Text>
        <div style={{ marginTop: 8 }}>
          <Checkbox.Group
            value={shown as string[]}
            options={options}
            onChange={(next) => onChange(spec.keys.filter((k) => !next.includes(k)))}
          />
        </div>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
          {spec.help}
        </Typography.Paragraph>
      </div>
    );
  }

  const restricted = Array.isArray(value);
  return (
    <div style={{ marginBottom: 18 }}>
      <Typography.Text strong>{spec.label}</Typography.Text>
      <div style={{ marginTop: 8 }}>
        <Radio.Group
          value={restricted ? 'some' : 'all'}
          onChange={(e) => onChange(e.target.value === 'all' ? null : [...spec.keys])}
          optionType="button"
          buttonStyle="solid"
          size="small"
          options={[
            { value: 'all', label: 'All' },
            { value: 'some', label: 'Only selected' },
          ]}
        />
      </div>
      {restricted && (
        <div style={{ marginTop: 10 }}>
          <Checkbox.Group
            value={value as string[]}
            options={options}
            onChange={(next) => onChange(spec.keys.filter((k) => next.includes(k)))}
          />
        </div>
      )}
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
        {spec.help}{' '}
        {restricted ? (
          <>
            Fixed to this selection: anything the platform adds later stays hidden from them
            until someone ticks it here.
          </>
        ) : (
          <>
            <strong>All</strong> follows the platform — anything added later appears for them
            automatically, which ticking every box would not do.
          </>
        )}
      </Typography.Paragraph>
    </div>
  );
}

function VisibilityCell({
  page,
  role,
  disabled,
}: {
  page: PlatformPage;
  role: EffectiveRole;
  disabled: string[];
}) {
  const visibility = pageVisibility(page, role, disabled);

  if (visibility === 'visible') {
    return (
      <Tooltip title={page.note ?? 'Visible in the sidebar.'}>
        <CheckOutlined style={{ color: '#16a34a' }} />
      </Tooltip>
    );
  }
  if (visibility === 'module-off') {
    return (
      <Tooltip
        title={`${MODULE_LABEL[page.module as string]} is switched off for this organization, so this page is hidden from everyone in it.`}
      >
        <Tag style={{ margin: 0, fontSize: 11 }}>Page off</Tag>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={`The platform hides this page from ${PLATFORM_ROLE_LABEL[role].toLowerCase()}s, for every customer. Not configurable here.`}
    >
      <MinusOutlined style={{ color: '#d1d5db' }} />
    </Tooltip>
  );
}

/* -------------------------------------------------------------------------- */
/* Creating                                                                    */
/* -------------------------------------------------------------------------- */

function CreateOrgModal({
  open,
  onClose,
  onCreated,
  countries,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  countries: CountryOption[];
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [enabledPages, setEnabledPages] = useState<string[]>([...GATEABLE_MODULES]);
  const tenants = useAsync(() => tenantApi.list({ per_page: 100 }), []);
  // Watched rather than read on submit: the default-market options narrow to
  // whatever Markets currently holds, so the form cannot offer a default the
  // API would reject for being outside the list.
  const allowedDraft = Form.useWatch<string[] | undefined>(
    'allowed_countries',
    form,
  );

  // A tenant can whitelabel at most one org (1:1), so offering an already-taken
  // tenant here would only produce a server-side rejection.
  const availableTenants = (tenants.data?.items ?? []).filter((t) => t.org_id === null);

  // The modal stays mounted between opens, so a cancelled create must not seed
  // the next one.
  useEffect(() => {
    if (open) setEnabledPages([...GATEABLE_MODULES]);
  }, [open]);

  async function onSubmit() {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const result = await orgApi.create({
        name: values.name.trim(),
        org_type: values.org_type,
        // The org's default market: its default workspace (D6) takes it, and
        // so does every later workspace and signup that does not name one. The
        // tenant's `default_country` used to supply this and no longer exists.
        default_country: values.default_country,
        // null (the field left empty) means every selectable market, which is
        // the right default: the org then picks up a new market the day it
        // ships, where a list naming today's would freeze it.
        allowed_countries: values.allowed_countries?.length
          ? values.allowed_countries
          : null,
        tenant_id: values.tenant_id ?? null,
        // Set here rather than left to the drawer, unlike the other product
        // settings: the org's first admin is usually provisioned within
        // minutes of this form, and getting it wrong means someone has to go
        // and correct that person's account by hand.
        default_language: values.default_language ?? 'en-US',
        // Only pages are offered here. Metrics, charts, campaign types and
        // inventory fields start unrestricted and are set from the drawer:
        // asking for all six at provisioning time is a form nobody fills in
        // correctly, and every one of them can be changed a minute later.
        features: {
          disabled_modules: GATEABLE_MODULES.filter((m) => !enabledPages.includes(m)),
        },
      });
      message.success(
        `Created ${result.organization.name} with default workspace "${result.default_workspace.slug}"`,
      );
      form.resetFields();
      onCreated();
      onClose();
    } catch {
      /* toast already shown */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Create organization"
      open={open}
      onCancel={onClose}
      onOk={onSubmit}
      okText="Create"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Name is required' }]}
          extra="The default workspace takes this name, and its slug is frozen at creation."
        >
          <Input placeholder="Acme Media" />
        </Form.Item>
        <Form.Item
          name="org_type"
          label="Type"
          rules={[{ required: true, message: 'Pick a type' }]}
        >
          <Select
            options={(Object.keys(ORG_TYPE_LABEL) as OrgType[]).map((value) => ({
              value,
              label: ORG_TYPE_LABEL[value],
            }))}
          />
        </Form.Item>
        <Form.Item
          name="allowed_countries"
          label="Markets"
          extra="Which measurement markets this org's workspaces may use. Leave empty for all of them — that way the org picks up a new market the day it ships, where a list naming today's would freeze it."
        >
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder="All markets"
            loading={!countries.length}
            options={countries.map((c) => ({
              value: c.iso2,
              label: `${c.name} (${c.iso2})`,
            }))}
            onChange={(next: string[]) => {
              // Keep the default inside the list; the API refuses it otherwise.
              const current = form.getFieldValue('default_country');
              if (next.length && current && !next.includes(current)) {
                form.setFieldValue('default_country', undefined);
              }
            }}
          />
        </Form.Item>
        <Form.Item
          name="default_language"
          label="Default language"
          initialValue="en-US"
          extra="The language new members of this org start the console in — the first admin you provision next, and every self-serve signup on its subdomain. Each person can change their own afterwards on their account page."
        >
          <Select options={ORG_LANGUAGE_OPTIONS} />
        </Form.Item>
        <Form.Item
          name="default_country"
          label="Default market"
          rules={[{ required: true, message: 'A default market is required' }]}
          extra="What a workspace gets when nobody names one — this org's default workspace, and every self-serve signup on its subdomain, which has no way to ask. Its inventory is measured against this market's model."
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select the default market"
            loading={!countries.length}
            options={countries
              .filter((c) => !allowedDraft?.length || allowedDraft.includes(c.iso2))
              .map((c) => ({ value: c.iso2, label: `${c.name} (${c.iso2})` }))}
          />
        </Form.Item>
        <Form.Item
          name="tenant_id"
          label="Whitelabel tenant"
          extra="Optional. Leave empty for an org on the bare Seiki domain. Branding only — it has no say in what the org can reach."
        >
          <Select
            allowClear
            loading={tenants.loading}
            placeholder="No whitelabel (bare domain)"
            options={availableTenants.map((t) => ({
              value: t.id,
              label: `${t.subdomain} — ${t.name}`,
            }))}
            notFoundContent="Every tenant is already attached to an org"
          />
        </Form.Item>
        <Form.Item
          label="Pages its members can see"
          extra="Metrics, charts, campaign types and inventory fields start unrestricted — set those from the org's Product scope drawer once it exists."
        >
          <Checkbox.Group
            value={enabledPages}
            onChange={(next) => setEnabledPages(next as string[])}
            options={GATEABLE_MODULES.map((key) => ({ value: key, label: MODULE_LABEL[key] }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function AttachTenantModal({
  org,
  onClose,
  onAttached,
}: {
  org: Organization | null;
  onClose: () => void;
  onAttached: () => void;
}) {
  const [tenantId, setTenantId] = useState<number | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const tenants = useAsync(() => tenantApi.list({ per_page: 100 }), []);

  const availableTenants = (tenants.data?.items ?? []).filter((t: Tenant) => t.org_id === null);

  async function onSubmit() {
    if (!org || !tenantId) return;
    setSubmitting(true);
    try {
      await orgApi.attachToTenant(tenantId, org.id);
      message.success('Tenant attached');
      setTenantId(undefined);
      onAttached();
      onClose();
    } catch {
      /* toast already shown */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={org ? `Attach a tenant to ${org.name}` : 'Attach tenant'}
      open={org !== null}
      onCancel={onClose}
      onOk={onSubmit}
      okText="Attach"
      okButtonProps={{ disabled: !tenantId }}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="One tenant, one org"
        description="Attaching gives this org the tenant's branding: colours, logo and terminology. The relationship is 1:1 and can only be changed by detaching first."
      />
      <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
        Only tenants not already attached to an org are listed. It changes nothing about what
        the org can reach — product scope is held on the organization and nowhere else.
      </Typography.Paragraph>
      <Select
        style={{ width: '100%' }}
        loading={tenants.loading}
        placeholder="Select tenant"
        value={tenantId}
        onChange={setTenantId}
        options={availableTenants.map((t) => ({
          value: t.id,
          label: `${t.subdomain} — ${t.name}`,
        }))}
        notFoundContent="Every tenant is already attached to an org"
      />
    </Modal>
  );
}
